/**
 * Générateur — Définir la notion de nombre positif, strictement positif,
 * négatif, strictement négatif.
 * Attendu ID : 704 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 1)
 *
 * Plan de cartes (validé) :
 *
 *   Carte « signe » — Créé n°10 Classer les nombres
 *     classer (catégories « Nombres positifs / Nombres négatifs / Positif ET
 *     négatif ») + tableau colonnesLibres (3 colonnes exclusives, une coche
 *     par ligne). LE 0 EST L'ENJEU : présent à chaque tirage, seul dans sa
 *     catégorie — conformément au cadrage (0 à la fois positif et négatif).
 *
 *   Carte « strict » — Créé n°11 Strictement ou pas ?
 *     tableau_vf (la paire discriminante « 0 est strictement positif » FAUX /
 *     « 0 est positif » VRAI, + « tout positif est strictement positif »
 *     FAUX) et qcm_multiple (coche les k nombres strictement positifs /
 *     négatifs, 0 en piège, compte annoncé calculé).
 *
 * Convention enseignée (cadrage) : positif = ≥ 0, négatif = ≤ 0 ;
 * « strictement » exclut 0. Signe « - » = tiret clavier partout.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeClasser, basePayload,
} from '../../../utils';

const ATTENDU_ID = 704;

const L_SIGNE = 'Classer les nombres (positif, négatif… ou les deux)';
const L_STRICT = 'Strictement ou pas ?';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const dix = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};

/**
 * Tire k valeurs positives et k négatives (dixièmes), valeurs absolues toutes
 * distinctes ; certains positifs sont affichés avec un « + » (rotation).
 */
const tirerNombres = (k) => {
    const absolues = new Set();
    for (let essai = 0; essai < 80 && absolues.size < 2 * k; essai++) {
        const decimal = randomChoice([true, false]);
        let a = decimal ? randInt(11, 99) : randInt(1, 25) * 10;
        if (decimal && a % 10 === 0) a += 3;
        absolues.add(a);
    }
    [70, 130, 250, 340, 410, 580].forEach(a => { if (absolues.size < 2 * k) absolues.add(a); });
    const liste = shuffle([...absolues]).slice(0, 2 * k);
    const positifs = liste.slice(0, k).map(a =>
        (randomChoice([true, false]) ? `+${dix(a)}` : dix(a)));
    const negatifs = liste.slice(k).map(a => dix(-a));
    return { positifs, negatifs };
};

// ═════════ Carte 1 — Classer les nombres (Créé n°10) ═════════

const CAT_POS = 'Nombres positifs';
const CAT_NEG = 'Nombres négatifs';
const CAT_DEUX = 'Positif ET négatif';

const genSigneClasser = () => {
    const { positifs, negatifs } = tirerNombres(3);
    const payload = makeClasser(
        [
            { name: CAT_POS, items: positifs },
            { name: CAT_NEG, items: negatifs },
            { name: CAT_DEUX, items: ['0'] },
        ],
        '<p><b>Classe chaque nombre dans la bonne colonne.</b></p>',
        opts(L_SIGNE));
    payload._meta.params = { positifs, negatifs };
    return payload;
};

const genSigneTableau = () => {
    const { positifs, negatifs } = tirerNombres(2);
    const colonnes = ['Positif', 'Négatif', 'Positif et négatif'];
    const entrees = shuffle([
        ...positifs.map(n => [n, 0]),
        ...negatifs.map(n => [n, 1]),
        ['0', 2],
    ]);
    const lignes = entrees.map(e => e[0]);
    const bonnes = entrees.map(e => e[1]);
    const tableEl = {
        type: 'table',
        config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses: bonnes },
    };
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnes[i]]}]`).join('');
    const payload = basePayload('qcm',
        '<p><b>Pour chaque nombre, coche la bonne colonne.</b></p>',
        { choix: [], bonne_reponse: br },
        opts(L_SIGNE),
        { version: 1, elements: [tableEl] });
    payload._meta.params = { lignes };
    return payload;
};

// ═════════ Carte 2 — Strictement ou pas ? (Créé n°11) ═════════

const genStrictVf = () => {
    const n = randInt(2, 30);
    let d = randInt(11, 99);
    if (d % 10 === 0) d += 3;

    const pool = [
        [`-${n} est strictement négatif.`, 0],
        [`${dix(d)} est strictement positif.`, 0],
        ['0 est positif.', 0],
        ['0 est négatif.', 0],
        ['0 est strictement positif.', 1],
        ['0 est strictement négatif.', 1],
        ['Tout nombre positif est strictement positif.', 1],
        [`-${dix(d)} est strictement positif.`, 1],
    ];
    const vraies = pool.filter(l => l[1] === 0);
    const fausses = pool.filter(l => l[1] === 1);
    const lignes = shuffle([...shuffle(vraies).slice(0, 2), ...shuffle(fausses).slice(0, 2)]);
    const payload = makeVraiFaux(
        lignes.map(l => l[0]),
        lignes.map(l => l[1]),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b> '
        + '(Rappel : 0 est à la fois positif et négatif.)</p>',
        opts(L_STRICT));
    payload._meta.params = { n, d };
    return payload;
};

const MOTS_COMPTE = { 1: 'le seul nombre qui est', 2: 'les deux nombres qui sont', 3: 'les trois nombres qui sont' };

const genStrictQcmMulti = () => {
    const strictementPositifs = randomChoice([true, false]);
    const k = randomChoice([2, 3]);
    const { positifs, negatifs } = tirerNombres(3);
    const corrects = (strictementPositifs ? positifs : negatifs).slice(0, k);
    const autres = (strictementPositifs ? negatifs : positifs).slice(0, 5 - k);
    const choix = [...corrects, ...autres, '0']; // 0 : LE piège (positif mais pas strictement)
    const payload = makeQcm(choix, corrects,
        `<p><b>Coche ${MOTS_COMPTE[k]} strictement ${strictementPositifs ? 'positifs' : 'négatifs'}.</b></p>`,
        opts(L_STRICT), true);
    payload._meta.params = { corrects, strictementPositifs };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    signe_classer: genSigneClasser,
    signe_tableau: genSigneTableau,
    strict_vf: genStrictVf,
    strict_qcm_multiple: genStrictQcmMulti,
};

const FORMAT_LABELS = {
    classer: 'Classer', tableau: 'Tableau', vf: 'Vrai/Faux', qcm_multiple: 'QCM Multi',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'signe', label: L_SIGNE, formatsList: buildFormats('signe') },
    { id: 'strict', label: L_STRICT, formatsList: buildFormats('strict') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
